<script>
  /**
   * Contratos de prestación de servicios.
   *
   * ── QUÉ RESUELVE ESTA PANTALLA ──────────────────────────────────────────────
   *
   * Un contratista no tiene quien le descuente nada. Cada mes tiene que
   * acordarse solo de tres cosas, y la tercera casi nadie la sabe:
   *
   *   1. Cuánto le entra.
   *   2. Cuánto apartar para salud, pensión y ARL.
   *   3. ANTES DE QUÉ DÍA pagarlo — y eso depende de los dos últimos dígitos
   *      de su cédula, con días hábiles, festivos incluidos.
   *
   * Guardando el contrato una vez, la app calcula las tres solo.
   */
  import { formatear, interpretar } from '../lib/dinero.js';
  import { crearContrato, hoy } from '../lib/db.js';
  import { guardar, borrar } from '../lib/repositorio.js';
  import { aportesOps, fechaLimitePila, SEGURIDAD_SOCIAL_OPS, APARTAR_OPS } from '../lib/parametros.js';

  let { abierta = $bindable(false), contratos = [], cuentas = [], alCambiar = () => {} } = $props();

  let editando = $state(null);
  let valorMensualTexto = $state('');
  let guardando = $state(false);
  let confirmandoBorrado = $state(false);

  const activos = $derived(contratos.filter((c) => c.activo && !c.borrado_en));

  function nuevo() {
    editando = crearContrato({ fecha_inicio: hoy() });
    valorMensualTexto = '';
    confirmandoBorrado = false;
  }

  function abrir(c) {
    editando = { ...c };
    valorMensualTexto = c.valor_mensual ? formatear(c.valor_mensual, { simbolo: false }) : '';
    confirmandoBorrado = false;
  }

  const valorMensual = $derived(interpretar(valorMensualTexto) ?? 0);

  /** Lo que hay que apartar, con el piso del salario mínimo ya aplicado. */
  const aportes = $derived(valorMensual > 0 ? aportesOps(valorMensual) : null);

  /** Cuándo vence el pago de los aportes del mes pasado. */
  const limite = $derived.by(() => {
    if (!editando?.cedula_ultimos_2 || editando.cedula_ultimos_2.length < 2) return null;
    const d = new Date();
    // Se paga MES VENCIDO: en octubre se pagan los aportes de septiembre.
    const mesAnterior = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const periodo = `${mesAnterior.getFullYear()}-${String(mesAnterior.getMonth() + 1).padStart(2, '0')}`;
    return fechaLimitePila(editando.cedula_ultimos_2, periodo);
  });

  function fechaBonita(iso) {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-CO', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
  }

  async function aplicar() {
    if (guardando || !editando?.nombre?.trim()) return;
    guardando = true;
    try {
      await guardar('contratos', { ...editando, valor_mensual: valorMensual });
      editando = null;
      alCambiar();
    } finally {
      guardando = false;
    }
  }

  async function eliminar() {
    if (guardando || !editando?.id) return;
    guardando = true;
    try {
      await borrar('contratos', editando.id);
      editando = null;
      alCambiar();
    } finally {
      guardando = false;
    }
  }
</script>

{#if abierta}
  <div class="pantalla">
    <header>
      <button class="volver" onclick={() => { if (editando) editando = null; else abierta = false; }} aria-label="Volver">←</button>
      <h1>{editando ? (editando.id && contratos.some(c => c.id === editando.id) ? 'Editar contrato' : 'Nuevo contrato') : 'Contratos'}</h1>
      {#if !editando}
        <button class="agregar" onclick={nuevo}>+ Agregar</button>
      {/if}
    </header>

    <div class="contenido">
      {#if !editando}
        {#if !activos.length}
          <div class="vacio">
            <p class="vacio-titulo">No tienes contratos guardados</p>
            <p class="explica">
              Si trabajas por prestación de servicios, guarda tu contrato aquí.
              La app calcula cuánto apartar para seguridad social y, sobre todo,
              <strong>hasta qué día tienes para pagarla</strong> — que depende de
              tu cédula y casi nadie lo sabe.
            </p>
          </div>
        {:else}
          {#each activos as c (c.id)}
            <button class="tarjeta" onclick={() => abrir(c)}>
              <div class="cabeza">
                <span class="nombre">{c.nombre}</span>
                <span class="cifra valor">{formatear(c.valor_mensual)}</span>
              </div>
              <span class="contratante">{c.contratante}</span>
              {#if c.valor_mensual > 0}
                {@const a = aportesOps(c.valor_mensual)}
                <div class="resumen-tarjeta">
                  <span>Apartar cada mes: <strong class="cifra">{formatear(a.total)}</strong></span>
                  <span>Te queda libre: <strong class="cifra">{formatear(a.neto)}</strong></span>
                </div>
              {/if}
            </button>
          {/each}
        {/if}

      {:else}
        <label>
          <span class="etiqueta">Nombre del contrato</span>
          <input type="text" bind:value={editando.nombre} placeholder="Asamblea de Córdoba 2026" />
        </label>

        <label>
          <span class="etiqueta">Quién te paga</span>
          <input type="text" bind:value={editando.contratante} placeholder="Entidad contratante" />
        </label>

        <label>
          <span class="etiqueta">Número del contrato</span>
          <input type="text" bind:value={editando.numero} placeholder="Opcional" />
        </label>

        <label>
          <span class="etiqueta">Valor mensual</span>
          <input type="text" inputmode="numeric" bind:value={valorMensualTexto} placeholder="0" />
          <span class="ayuda">Lo que te consignan cada mes, antes de descuentos.</span>
        </label>

        <div class="par">
          <label>
            <span class="etiqueta">Empieza</span>
            <input type="date" bind:value={editando.fecha_inicio} />
          </label>
          <label>
            <span class="etiqueta">Termina</span>
            <input type="date" bind:value={editando.fecha_fin} />
          </label>
        </div>

        <label>
          <span class="etiqueta">Los dos últimos dígitos de tu cédula</span>
          <input
            type="text"
            inputmode="numeric"
            maxlength="2"
            bind:value={editando.cedula_ultimos_2}
            placeholder="00"
          />
          <span class="ayuda">
            Solo esos dos. Son los que definen tu fecha límite de pago, y es
            el único dato de la cédula que la app necesita.
          </span>
        </label>

        <!-- Lo que la app calcula, en vivo mientras se escribe -->
        {#if aportes}
          <section class="calculo">
            <h2>Lo que te toca apartar</h2>

            <div class="linea">
              <span>Base de cotización (40% del contrato)</span>
              <span class="cifra">{formatear(Math.round(valorMensual * 0.4))}</span>
            </div>

            {#if aportes.ajustadoAlPiso}
              <p class="nota-piso">
                Ese 40% queda por debajo del salario mínimo, así que por ley
                cotizas sobre el mínimo: <strong class="cifra">{formatear(aportes.ibc)}</strong>.
                Por eso el aporte pesa más de lo que uno esperaría.
              </p>
            {/if}

            <div class="linea"><span>Salud (12,5%)</span><span class="cifra">{formatear(aportes.salud)}</span></div>
            <div class="linea"><span>Pensión (16%)</span><span class="cifra">{formatear(aportes.pension)}</span></div>
            <div class="linea"><span>ARL</span><span class="cifra">{formatear(aportes.arl)}</span></div>

            <div class="linea total">
              <span>Total al mes</span>
              <span class="cifra">{formatear(aportes.total)}</span>
            </div>
            <div class="linea">
              <span>Te queda libre</span>
              <span class="cifra">{formatear(aportes.neto)}</span>
            </div>

            {#if limite?.fecha}
              <div class="fecha-limite">
                <span class="rotulo-limite">Tu fecha límite este mes</span>
                <span class="fecha">{fechaBonita(limite.fecha)}</span>
                <span class="ayuda">
                  Te toca el día hábil {limite.diaHabil}, por los dígitos
                  {editando.cedula_ultimos_2} de tu cédula. {limite.nota}
                </span>
              </div>
            {/if}

            <p class="nota-prima">
              Ojo: como contratista <strong>no tienes prima ni cesantías</strong>.
              Si quieres quedar como un empleado, aparta otro
              {formatear(Math.round(valorMensual * APARTAR_OPS.prima_equivalente))} para
              cada una.
            </p>
          </section>
        {/if}

        {#if !confirmandoBorrado}
          <div class="botones">
            {#if contratos.some((c) => c.id === editando.id)}
              <button class="borrar" onclick={() => (confirmandoBorrado = true)}>Borrar</button>
            {/if}
            <button class="principal" disabled={!editando.nombre?.trim() || guardando} onclick={aplicar}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        {:else}
          <p class="confirma">¿Seguro? No se puede deshacer.</p>
          <div class="botones">
            <button class="secundario" onclick={() => (confirmandoBorrado = false)}>No, cancelar</button>
            <button class="borrar-firme" disabled={guardando} onclick={eliminar}>Sí, borrar</button>
          </div>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .pantalla {
    position: fixed;
    inset: 0;
    z-index: var(--z-hoja);
    background: var(--bg-canvas);
    overflow-y: auto;
    max-width: var(--w-app);
    margin-inline: auto;
    animation: entrar var(--d-normal) var(--ease-entrada);
  }
  @keyframes entrar {
    from { opacity: 0; transform: translateX(16px) }
    to   { opacity: 1; transform: translateX(0) }
  }

  header {
    position: sticky;
    top: 0;
    z-index: var(--z-pegajoso);
    display: flex;
    align-items: center;
    gap: var(--e-2);
    padding: calc(var(--e-3) + var(--safe-t)) var(--gutter) var(--e-3);
    background: var(--bg-canvas);
    border-bottom: 1px solid var(--border);
  }
  .volver {
    width: var(--tap);
    height: var(--tap);
    display: grid;
    place-items: center;
    font-size: var(--t-xl);
    border-radius: var(--r-full);
    margin-left: calc(var(--e-3) * -1);
  }
  h1 { font-size: var(--t-lg); font-weight: var(--w-semibold); flex: 1 }
  .agregar {
    font-size: var(--t-sm);
    font-weight: var(--w-semibold);
    color: var(--acento);
    background: var(--acento-suave);
    padding: var(--e-2) var(--e-3);
    border-radius: var(--r-full);
    min-height: var(--tap-min);
  }

  .contenido {
    padding: var(--e-4) var(--gutter) calc(var(--e-16) + var(--safe-b));
    display: flex;
    flex-direction: column;
    gap: var(--e-3);
  }

  .explica { font-size: var(--t-sm); color: var(--ink-2); line-height: var(--lh-loose) }
  .ayuda { font-size: var(--t-xs); color: var(--ink-3); line-height: var(--lh-normal) }

  .vacio { padding: var(--e-10) 0; text-align: center }
  .vacio-titulo {
    font-size: var(--t-lg);
    font-weight: var(--w-semibold);
    color: var(--ink-2);
    margin-bottom: var(--e-2);
  }

  /* ── Tarjeta de contrato ── */
  .tarjeta {
    display: flex;
    flex-direction: column;
    gap: var(--e-1);
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
    text-align: left;
    width: 100%;
  }
  .cabeza { display: flex; justify-content: space-between; gap: var(--e-2); align-items: baseline }
  .nombre { font-weight: var(--w-semibold) }
  .valor { font-weight: var(--w-bold); white-space: nowrap }
  .contratante { font-size: var(--t-sm); color: var(--ink-3) }
  .resumen-tarjeta {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: var(--t-xs);
    color: var(--ink-2);
    margin-top: var(--e-2);
    padding-top: var(--e-2);
    border-top: 1px solid var(--border);
  }

  /* ── Formulario ── */
  label { display: flex; flex-direction: column; gap: var(--e-1) }
  .etiqueta { font-size: var(--t-sm); font-weight: var(--w-medium) }
  .par { display: grid; grid-template-columns: 1fr 1fr; gap: var(--e-3) }

  input {
    padding: var(--e-3);
    font-size: var(--t-base);
    background: var(--bg-surface);
    border: 1px solid var(--border-strong);
    border-radius: var(--r-md);
    min-height: var(--tap);
    width: 100%;
  }

  /* ── El cálculo ── */
  .calculo {
    display: flex;
    flex-direction: column;
    gap: var(--e-2);
    padding: var(--e-4);
    background: var(--bg-surface);
    border: 1px solid var(--border);
    border-radius: var(--r-lg);
  }
  h2 {
    font-size: var(--t-xs);
    font-weight: var(--w-semibold);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--ink-3);
  }
  .linea {
    display: flex;
    justify-content: space-between;
    gap: var(--e-2);
    font-size: var(--t-sm);
    color: var(--ink-2);
  }
  .linea.total {
    font-weight: var(--w-bold);
    color: var(--ink-1);
    font-size: var(--t-base);
    padding-top: var(--e-2);
    border-top: 1px solid var(--border);
  }

  .nota-piso {
    font-size: var(--t-xs);
    line-height: var(--lh-loose);
    color: var(--alerta);
    background: var(--alerta-suave);
    padding: var(--e-2);
    border-radius: var(--r-sm);
  }

  .fecha-limite {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: var(--e-2);
    padding: var(--e-3);
    background: var(--acento-suave);
    border-radius: var(--r-md);
  }
  .rotulo-limite {
    font-size: var(--t-2xs);
    text-transform: uppercase;
    letter-spacing: var(--track-wide);
    color: var(--acento);
  }
  .fecha {
    font-size: var(--t-lg);
    font-weight: var(--w-bold);
    color: var(--acento);
    text-transform: capitalize;
  }

  .nota-prima {
    font-size: var(--t-xs);
    color: var(--ink-3);
    line-height: var(--lh-loose);
    padding-top: var(--e-2);
    border-top: 1px solid var(--border);
  }

  /* ── Botones ── */
  .botones { display: flex; gap: var(--e-2); margin-top: var(--e-2) }
  .principal {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--acento);
    color: var(--on-acento);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .principal:disabled { opacity: 0.4 }
  .secundario {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--bg-sunken);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .borrar {
    padding: var(--e-3) var(--e-4);
    border-radius: var(--r-md);
    color: var(--peligro);
    background: var(--peligro-suave);
    font-weight: var(--w-medium);
    min-height: var(--tap);
  }
  .borrar-firme {
    flex: 1;
    padding: var(--e-3);
    border-radius: var(--r-md);
    background: var(--peligro);
    color: var(--on-peligro);
    font-weight: var(--w-semibold);
    min-height: var(--tap);
  }
  .confirma { font-size: var(--t-sm); color: var(--peligro); text-align: center }
</style>
